/**
 * Getting files back, files first (docs/restore-files-first.md). A tree of everything a computer's kept
 * backups still hold, the newest copy of each file by default, older copies offered only where a file has
 * them, and the day list for bringing a folder back as it was before something went wrong.
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { get, qs } from '../api';
import { arrow, isRtl, locale, tr } from '../i18n';
import { useTenant } from '../session';
import { bytes, Button, Empty, ErrorBox, Icons, Loading, vdisplay, when } from '../ui';

export interface KeptEntry {
  id: number;
  path: string;
  name: string;
  kind: string;
  size: number;
  mtime: string | null;
  /** How many different copies of this file are kept. */
  versions: number;
  /** The newest kept backup holding this copy: what a restore of it names. */
  seq: number;
  /** No longer on the computer; deleted_at is the first backup that did not have it. */
  deleted: boolean;
  deleted_at: string | null;
}

/** One ticked file or folder, and the kept backup its copy comes from. */
export interface Choice {
  path: string;
  seq: number;
  kind: string;
  /** Only when an older copy was picked: when that copy was saved. */
  olderFrom?: string | null;
}
export type Choices = Map<string, Choice>;

const under = (path: string, folder: string) => folder === '/' || path.startsWith(folder + '/');

/** Tick or untick; ticking a folder replaces anything already ticked inside it. */
export function toggleChoice(choices: Choices, e: Pick<KeptEntry, 'path' | 'seq' | 'kind'>): Choices {
  const n = new Map(choices);
  if (n.has(e.path)) n.delete(e.path);
  else return setChoice(n, { path: e.path, seq: e.seq, kind: e.kind });
  return n;
}

export function setChoice(choices: Choices, c: Choice): Choices {
  const n = new Map(choices);
  for (const k of [...n.keys()]) if (k !== c.path && under(k, c.path)) n.delete(k);
  n.set(c.path, c);
  return n;
}

export const shortDate = (iso: string) => new Date(iso).toLocaleDateString(locale(), { day: 'numeric', month: 'short' });

export const dayLabel = (iso: string) => {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(Date.now() - 86_400_000);
  if (d.toDateString() === today.toDateString()) return tr('Today');
  if (d.toDateString() === yesterday.toDateString()) return tr('Yesterday');
  return d.toLocaleDateString(locale(), { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};
export const timeOf = (iso: string) => new Date(iso).toLocaleTimeString(locale(), { hour: '2-digit', minute: '2-digit' });

interface TreeCtx {
  deviceId: string;
  pathStyle: 'windows' | 'posix';
  choices: Choices;
  open: Set<string>;
  focus: string | null;
  versionsOf: string | null;
  setFocus: (p: string) => void;
  toggleOpen: (path: string) => void;
  toggle: (e: KeptEntry) => void;
  chooseCopy: (c: Choice) => void;
  showVersions: (path: string | null) => void;
  register: (e: KeptEntry) => void;
}
const Ctx = createContext<TreeCtx | null>(null);

/**
 * The tree is one tab stop (roving focus): the arrow keys move and open folders (mirrored in Hebrew), Space
 * ticks, Enter opens a folder or a file's older copies.
 */
export function KeptFilesTree({ deviceId, pathStyle, rootLabel, choices, onChange }: {
  deviceId: string;
  pathStyle: 'windows' | 'posix';
  /** What the top level is called; a phone's files start at "My Files". */
  rootLabel?: string;
  choices: Choices;
  onChange: (next: Choices) => void;
}) {
  const t = useTenant();
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [focus, setFocus] = useState<string | null>(null);
  const [versionsOf, setVersionsOf] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [onlyDeleted, setOnlyDeleted] = useState(false);
  const tree = useRef<HTMLUListElement>(null);
  const known = useRef(new Map<string, KeptEntry>());

  // Skip through single-folder levels ("/" → "C:" → "Users" → …): the tree starts where there is a choice.
  const [top, setTop] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    setTop(null);
    (async () => {
      let p = '/';
      for (let i = 0; i < 16; i++) {
        const r = await get(`/api/t/${t.tenantId}/devices/${deviceId}/kept/browse${qs({ path: p, limit: 2 })}`);
        if (r.entries.length === 1 && r.entries[0].kind === 'd') p = r.entries[0].path;
        else break;
      }
      if (alive) setTop(p);
    })().catch(() => alive && setTop('/'));
    return () => { alive = false; };
  }, [deviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const searching = search.trim().length >= 2;
  const found = useQuery({
    queryKey: ['kept-search', deviceId, search.trim()],
    queryFn: () => get(`/api/t/${t.tenantId}/devices/${deviceId}/kept/search${qs({ q: search.trim() })}`),
    enabled: searching && !onlyDeleted,
  });
  const gone = useQuery({
    queryKey: ['kept-deleted', deviceId],
    queryFn: () => get(`/api/t/${t.tenantId}/devices/${deviceId}/kept/deleted`),
    enabled: onlyDeleted,
  });
  const flat: KeptEntry[] | null = onlyDeleted
    ? ((gone.data?.results ?? []) as KeptEntry[]).filter((e) => !searching || e.name.toLowerCase().includes(search.trim().toLowerCase()))
    : searching ? (found.data?.results ?? []) : null;

  const ctx: TreeCtx = {
    deviceId,
    pathStyle,
    choices,
    open,
    focus,
    versionsOf,
    setFocus,
    toggleOpen: (path) => {
      const n = new Set(open);
      if (n.has(path)) {
        n.delete(path);
        if (focus && under(focus, path) && focus !== path) setFocus(path);
      } else n.add(path);
      setOpen(n);
    },
    toggle: (e) => onChange(toggleChoice(choices, e)),
    chooseCopy: (c) => onChange(setChoice(choices, c)),
    showVersions: setVersionsOf,
    register: (e) => known.current.set(e.path, e),
  };

  const onKeyDown = (ev: KeyboardEvent<HTMLUListElement>) => {
    const el = ev.target as HTMLElement;
    if (el.getAttribute('role') !== 'treeitem' || !tree.current) return;
    const list = [...tree.current.querySelectorAll<HTMLElement>('[role="treeitem"]')];
    const i = list.indexOf(el);
    const path = el.dataset.path!;
    const e = known.current.get(path);
    const isDir = el.dataset.dir === '1';
    const isOpen = open.has(path);
    const go = (to: HTMLElement | null | undefined) => {
      if (!to) return;
      to.focus();
      setFocus(to.dataset.path!);
    };
    const openKey = isRtl() ? 'ArrowLeft' : 'ArrowRight';
    const closeKey = isRtl() ? 'ArrowRight' : 'ArrowLeft';
    switch (ev.key) {
      case 'ArrowDown': go(list[i + 1]); break;
      case 'ArrowUp': go(list[i - 1]); break;
      case 'Home': go(list[0]); break;
      case 'End': go(list.at(-1)); break;
      case openKey:
        if (isDir && !isOpen) ctx.toggleOpen(path);
        else if (isDir) go(list[i + 1]?.parentElement?.closest('[role="treeitem"]') === el ? list[i + 1] : null);
        break;
      case closeKey:
        if (isDir && isOpen) ctx.toggleOpen(path);
        else go(el.parentElement?.closest<HTMLElement>('[role="treeitem"]'));
        break;
      case ' ':
        if (e && el.getAttribute('aria-disabled') !== 'true') ctx.toggle(e);
        break;
      case 'Enter':
        if (isDir) ctx.toggleOpen(path);
        else if (e && e.versions > 1) setVersionsOf(versionsOf === path ? null : path);
        break;
      default:
        return;
    }
    ev.preventDefault();
  };

  const startLabel = top && top !== '/' ? vdisplay(top, pathStyle) : rootLabel ?? (pathStyle === 'windows' ? tr('This PC') : '/');
  return (
    <Ctx.Provider value={ctx}>
      <div className="card kept">
        <div className="card-head">
          <div className="small muted kept-start">
            {flat ? (onlyDeleted ? tr('Files that are no longer on the computer, most recent first') : tr('Search results')) : <>{tr('Starting at')} <bdi className={top && top !== '/' ? 'mono' : ''} dir={top && top !== '/' ? 'ltr' : undefined}>{startLabel}</bdi></>}
          </div>
          <div className="row">
            <button type="button" className={`filter-chip ${onlyDeleted ? 'on' : ''}`} aria-pressed={onlyDeleted} onClick={() => setOnlyDeleted(!onlyDeleted)}>{tr('Only deleted files')}</button>
            <input type="search" placeholder={tr('Search by file name…')} value={search} onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 220 }} aria-label={tr('Search everything that is kept')} />
          </div>
        </div>
        <div className="tree-wrap kept-wrap">
          <ul className="tree kept-tree" role="tree" aria-multiselectable="true" aria-label={tr('Files and folders')} ref={tree} onKeyDown={onKeyDown}>
            {flat ? (
              (onlyDeleted ? gone.isLoading : found.isLoading) ? <li role="none" className="card-pad"><Loading rows={3} /></li>
                : flat.length === 0 ? <li role="none"><Empty glyph="∅" title={onlyDeleted ? tr('No deleted files are kept') : tr('No matches')} /></li>
                : flat.map((e, i) => <Row key={e.id} e={e} depth={0} first={i === 0} flat />)
            ) : top === null ? <li role="none" className="card-pad"><Loading rows={3} /></li> : <Level path={top} depth={0} />}
          </ul>
        </div>
        <div className="picker-foot small muted">{tr('Arrow keys move and open folders, Space ticks, Enter shows older copies.')}</div>
      </div>
    </Ctx.Provider>
  );
}

function Level({ path, depth }: { path: string; depth: number }) {
  const c = useContext(Ctx)!;
  const t = useTenant();
  const q = useInfiniteQuery({
    queryKey: ['kept', c.deviceId, path],
    queryFn: ({ pageParam }) => get(`/api/t/${t.tenantId}/devices/${c.deviceId}/kept/browse${qs({ path, after: pageParam ?? undefined, limit: 200 })}`),
    initialPageParam: null as string | null,
    getNextPageParam: (last: { entries: KeptEntry[]; next: string | null }) => last.next,
  });
  const pad = { paddingInlineStart: 30 + depth * 18 };
  const entries: KeptEntry[] = q.data?.pages.flatMap((p) => p.entries) ?? [];
  if (q.isLoading) return <li role="none" className="small muted tree-msg" style={pad}>{tr('Opening…')}</li>;
  if (q.error) return <li role="none" style={pad}><ErrorBox error={q.error} retry={() => q.refetch()} /></li>;
  if (entries.length === 0) {
    return depth === 0
      ? <li role="none"><Empty glyph="◷" title={tr('Nothing is kept for this computer yet')}>{tr('Files can be brought back after a computer finishes its first backup.')}</Empty></li>
      : <li role="none" className="small muted tree-msg" style={pad}>{tr('This folder is empty')}</li>;
  }
  return (
    <>
      {entries.map((e, i) => <Row key={e.id} e={e} depth={depth} first={depth === 0 && i === 0} />)}
      {q.hasNextPage && <li role="none" style={pad} className="tree-msg"><Button size="sm" onClick={() => q.fetchNextPage()} busy={q.isFetchingNextPage}>{tr('Show more')}</Button></li>}
    </>
  );
}

function Row({ e, depth, first, flat }: { e: KeptEntry; depth: number; first?: boolean; flat?: boolean }) {
  const c = useContext(Ctx)!;
  c.register(e);
  const isDir = e.kind === 'd';
  const isOpen = c.open.has(e.path);
  const chosen = c.choices.get(e.path);
  const insideChosen = [...c.choices.keys()].some((k) => k !== e.path && under(e.path, k));
  const partly = !chosen && !insideChosen && [...c.choices.keys()].some((k) => k !== e.path && under(k, e.path));
  const checked = !!chosen || insideChosen;
  const name = flat ? vdisplay(e.path, c.pathStyle) : e.name;
  const goneText = e.deleted ? (e.deleted_at ? tr('deleted on {date}', { date: shortDate(e.deleted_at) }) : tr('deleted')) : '';
  const copyText = chosen?.olderFrom ? tr('Copy from {date}', { date: shortDate(chosen.olderFrom) }) : '';
  const label = [name, goneText, copyText].filter(Boolean).join(', ');
  return (
    <li
      role="treeitem"
      aria-level={depth + 1}
      aria-expanded={isDir ? isOpen : undefined}
      aria-checked={partly ? 'mixed' : checked}
      aria-disabled={insideChosen || undefined}
      aria-label={label}
      tabIndex={(c.focus ?? (first ? e.path : null)) === e.path ? 0 : -1}
      data-path={e.path}
      data-dir={isDir ? '1' : undefined}
      onFocus={(ev) => ev.target === ev.currentTarget && c.setFocus(e.path)}
    >
      <div className={`kept-row ${checked ? 'on' : ''} ${e.deleted ? 'gone' : ''}`} style={{ paddingInlineStart: 6 + depth * 18 }}>
        <span className="tree-twist" aria-hidden onClick={() => isDir && c.toggleOpen(e.path)}>{isDir ? (isOpen ? '▾' : arrow.crumb()) : ''}</span>
        <input type="checkbox" tabIndex={-1} aria-hidden checked={checked} disabled={insideChosen} ref={(el) => { if (el) el.indeterminate = partly; }} onChange={() => c.toggle(e)} />
        <span className="kept-name" onClick={() => (isDir ? c.toggleOpen(e.path) : !insideChosen && c.toggle(e))}>
          {isDir ? <Icons.folder className="ficon dir" /> : e.kind === 'l' || e.kind === 'j' ? <Icons.link className="ficon" /> : <Icons.file className="ficon" />}
          <bdi className={flat ? 'mono' : undefined} dir={flat ? 'ltr' : undefined}>{name}</bdi>
          {goneText && <span className="kept-gone small">{goneText}</span>}
          {copyText && <span className="chip">{copyText}</span>}
        </span>
        <span className="kept-size small num muted">{isDir ? '' : bytes(e.size)}</span>
        <span className="kept-date small muted">{!isDir && e.mtime ? when(e.mtime) : ''}</span>
        <span className="kept-act">
          {!isDir && e.versions > 1 && !insideChosen && (
            <button type="button" tabIndex={-1} className="kept-older small" aria-expanded={c.versionsOf === e.path} onClick={() => c.showVersions(c.versionsOf === e.path ? null : e.path)}>
              {tr('Older versions ({n})', { n: e.versions - 1 })}
            </button>
          )}
        </span>
      </div>
      {c.versionsOf === e.path && <Copies e={e} depth={depth} />}
      {isDir && isOpen && <ul role="group" className="tree"><Level path={e.path} depth={depth + 1} /></ul>}
    </li>
  );
}

/** The different kept copies of one file, inline under its row; the newest is already chosen. */
function Copies({ e, depth }: { e: KeptEntry; depth: number }) {
  const c = useContext(Ctx)!;
  const t = useTenant();
  const box = useRef<HTMLDivElement>(null);
  const v = useQuery({
    queryKey: ['versions', c.deviceId, e.path],
    queryFn: () => get(`/api/t/${t.tenantId}/devices/${c.deviceId}/versions${qs({ path: e.path })}`),
  });
  const copies: { id: number; size: number; mtime: string | null; seq: number; recovery_points: { finishedAt: string }[] }[] = v.data?.versions ?? [];
  const current = c.choices.get(e.path)?.seq ?? copies[0]?.seq;
  useEffect(() => {
    if (copies.length) box.current?.querySelector<HTMLInputElement>('input:checked, input')?.focus();
  }, [copies.length]);
  const close = () => {
    c.showVersions(null);
    document.querySelector<HTMLElement>(`[role="treeitem"][data-path="${CSS.escape(e.path)}"]`)?.focus();
  };
  const savedAt = (x: (typeof copies)[number]) => x.mtime ?? x.recovery_points.at(-1)?.finishedAt ?? null;
  return (
    <div
      ref={box}
      className="kept-copies"
      role="group"
      aria-label={tr('Copies of {name}', { name: e.name })}
      style={{ marginInlineStart: 52 + depth * 18 }}
      onKeyDown={(ev) => { if (ev.key === 'Escape') { ev.stopPropagation(); close(); } }}
    >
      {v.isLoading ? <Loading rows={2} /> : v.error ? <ErrorBox error={v.error} /> : (
        <>
          {copies.map((x, i) => (
            <label key={x.id} className="check">
              <input
                type="radio"
                name={`copy-${e.id}`}
                checked={current === x.seq}
                onChange={() => c.chooseCopy({ path: e.path, seq: x.seq, kind: e.kind, olderFrom: i === 0 ? null : savedAt(x) })}
              />
              <span>
                {i === 0 && <strong>{tr('Newest')} · </strong>}
                {savedAt(x) ? tr('Saved {time}', { time: when(savedAt(x)) }) : tr('Saved')} · {bytes(x.size)}
              </span>
            </label>
          ))}
          <div><Button size="sm" onClick={close}>{tr('Done')}</Button></div>
        </>
      )}
    </div>
  );
}

interface Backup { id: string; seq: number; finished_at: string; file_count: number; total_bytes: number; status: string }

/** "As it was on a certain day": one entry per day (its last backup), the other times that day on request. */
export function DayPicker({ deviceId, onPick, onCancel }: { deviceId: string; onPick: (b: Backup) => void; onCancel: () => void }) {
  const t = useTenant();
  const [more, setMore] = useState<Set<string>>(new Set());
  const rps = useInfiniteQuery({
    queryKey: ['rps-days', deviceId],
    queryFn: ({ pageParam }) => get(`/api/t/${t.tenantId}/devices/${deviceId}/recovery-points${qs({ limit: 200, beforeSeq: pageParam ?? undefined })}`),
    initialPageParam: null as number | null,
    getNextPageParam: (last: { recoveryPoints: Backup[]; nextBeforeSeq: number | null }) => last.nextBeforeSeq,
  });
  const days = useMemo(() => {
    const out: { day: string; items: Backup[] }[] = [];
    for (const r of (rps.data?.pages.flatMap((p) => p.recoveryPoints) ?? []) as Backup[]) {
      if (!r.finished_at) continue;
      const day = dayLabel(r.finished_at);
      const g = out.at(-1);
      if (g && g.day === day) g.items.push(r);
      else out.push({ day, items: [r] });
    }
    return out;
  }, [rps.data]);
  const pick = (b: Backup, text: string) => (
    <button type="button" key={b.id} className="day-pick" onClick={() => onPick(b)}>
      <strong>{text}</strong>
      <span className="small muted">{tr('{files} files · {size}', { files: Number(b.file_count).toLocaleString(locale()), size: bytes(b.total_bytes) })}{b.status === 'partial' ? ` · ${tr('some files were skipped')}` : ''}</span>
    </button>
  );
  return (
    <div className="card card-pad">
      <div className="row between">
        <h3>{tr('As it was on which day?')}</h3>
        <Button size="sm" variant="ghost" onClick={onCancel}>{tr('Cancel')}</Button>
      </div>
      <p className="small muted" style={{ marginTop: 4 }}>{tr('Each day shows its last backup. The files will show exactly as they were then, and only what is ticked comes back.')}</p>
      {rps.isLoading ? <Loading /> : rps.error ? <ErrorBox error={rps.error} /> : days.length === 0 ? <Empty glyph="◷" title={tr('Nothing to get back yet')} /> : (
        <div className="day-list">
          {days.map((g) => (
            <div key={g.day} className="day-group">
              {pick(g.items[0]!, `${g.day}, ${timeOf(g.items[0]!.finished_at)}`)}
              {g.items.length > 1 && (more.has(g.day)
                ? <div className="day-more">{g.items.slice(1).map((b) => pick(b, timeOf(b.finished_at)))}</div>
                : <button type="button" className="kept-older small" onClick={() => setMore(new Set(more).add(g.day))}>{tr('Other times that day ({n})', { n: g.items.length - 1 })}</button>)}
            </div>
          ))}
          {rps.hasNextPage && <Button size="sm" onClick={() => rps.fetchNextPage()} busy={rps.isFetchingNextPage}>{tr('Show earlier days')}</Button>}
        </div>
      )}
    </div>
  );
}
